"use client";

import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useTransition } from "react";
import { toast } from "sonner";
import { sendBuddyRequest } from "@/actions/buddy";
import { Button } from "@/components/ui/button";
import { Field, FieldContent, FieldError } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { updateDiscoveryRelationship } from "@/lib/buddy/discovery-cache";
import {
  invalidateBuddyRelationships,
  setBuddyRelationship,
} from "@/lib/buddy/relationship-cache";
import { cn } from "@/lib/utils";
import {
  type BuddyRequestInput,
  buddyRequestSchema,
} from "@/validations/buddy";
import { ActivityDetail } from "./activity-detail";
import { ActivitySelector, type EnrichedActivity } from "./activity-selector";
import { IntentSelector } from "./intent-selector";

type BuddyRequestFormProps = {
  targetUserId: string;
  enrichedActivities: EnrichedActivity[];
  isLoadingActivities?: boolean;
  onSuccess: () => void;
};

type BuddyRequestFormValues = {
  intent: BuddyRequestInput["intent"];
  message: string;
  activityId: string | undefined;
  activityNote: string;
};

const DEFAULT_VALUES: BuddyRequestFormValues = {
  intent: "activity",
  message: "",
  activityId: undefined,
  activityNote: "",
};

export function BuddyRequestForm({
  targetUserId,
  enrichedActivities,
  isLoadingActivities = false,
  onSuccess,
}: BuddyRequestFormProps) {
  const [isPending, startTransition] = useTransition();
  const queryClient = useQueryClient();

  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    onSubmit: async ({ value }) => {
      startTransition(async () => {
        const result = buddyRequestSchema.safeParse({
          intent: value.intent,
          message: value.message,
          activityId: value.activityId,
          activityNote: value.activityNote,
          targetUserId,
        });

        if (!result.success) {
          toast.error(result.error.issues[0]?.message ?? "Invalid request");
          return;
        }

        const response = await sendBuddyRequest(result.data);

        if (!response.ok) {
          toast.error(response.message ?? "We couldn't send that request");
          return;
        }

        setBuddyRelationship(queryClient, targetUserId, "request_sent");
        updateDiscoveryRelationship(queryClient, targetUserId, "request_sent");
        invalidateBuddyRelationships(queryClient, targetUserId);
        toast.success("Buddy request sent!");
        onSuccess();
      });
    },
  });

  return (
    <form
      id="buddy-request-form"
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <form.Field name="intent">
        {(intentField) => (
          <form.Field name="activityId">
            {(activityIdField) => {
              const selectedActivity = activityIdField.state.value
                ? enrichedActivities.find(
                    (activity) =>
                      activity.activity.id === activityIdField.state.value,
                  )
                : undefined;
              const showActivityDetail =
                intentField.state.value === "activity" && !!selectedActivity;

              if (showActivityDetail) {
                return (
                  <form.Field name="activityNote">
                    {(noteField) => (
                      <BuddyRequestStepLayout
                        contentClassName="px-1 py-2"
                        footer={<SubmitButton isPending={isPending} />}
                      >
                        <div className="animate-in duration-300 fade-in slide-in-from-right-4">
                          <ActivityDetail
                            activity={selectedActivity}
                            note={noteField.state.value ?? ""}
                            onNoteChange={noteField.handleChange}
                            onBack={() => {
                              activityIdField.handleChange(undefined);
                              noteField.handleChange("");
                            }}
                          />
                        </div>
                      </BuddyRequestStepLayout>
                    )}
                  </form.Field>
                );
              }

              return (
                <BuddyRequestStepLayout
                  header={
                    <IntentSelector
                      value={intentField.state.value}
                      onChange={(nextIntent) => {
                        intentField.handleChange(nextIntent);

                        if (nextIntent === "message") {
                          activityIdField.handleChange(undefined);
                          form.setFieldValue("activityNote", "");
                        } else {
                          form.setFieldValue("message", "");
                        }
                      }}
                    />
                  }
                  footer={
                    intentField.state.value === "message" ? (
                      <SubmitButton isPending={isPending} />
                    ) : undefined
                  }
                >
                  {intentField.state.value === "message" ? (
                    <div className="animate-in duration-300 fade-in slide-in-from-bottom-4">
                      <form.Field name="message">
                        {(field) => (
                          <Field
                            data-invalid={
                              field.state.meta.isTouched &&
                              field.state.meta.errors.length > 0
                            }
                          >
                            <FieldContent>
                              <label htmlFor={field.name} className="sr-only">
                                Message
                              </label>
                              <Textarea
                                id={field.name}
                                name={field.name}
                                placeholder="Hey! Saw you're also into padel—want to hit this weekend?"
                                value={field.state.value ?? ""}
                                onBlur={field.handleBlur}
                                onChange={(event) =>
                                  field.handleChange(event.target.value)
                                }
                                className="min-h-40 rounded-2xl border-border bg-surface px-4 py-3 text-text-primary transition-all focus:ring-2 focus:ring-brand/20"
                              />
                              <FieldError
                                errors={
                                  field.state.meta.errors.length > 0
                                    ? field.state.meta.errors
                                    : undefined
                                }
                              />
                            </FieldContent>
                          </Field>
                        )}
                      </form.Field>
                    </div>
                  ) : (
                    <div className="animate-in duration-300 fade-in slide-in-from-bottom-4">
                      <ActivitySelector
                        activities={enrichedActivities}
                        isLoading={isLoadingActivities}
                        onSelect={(id) => activityIdField.handleChange(id)}
                      />
                    </div>
                  )}
                </BuddyRequestStepLayout>
              );
            }}
          </form.Field>
        )}
      </form.Field>
    </form>
  );
}

type BuddyRequestStepLayoutProps = {
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  contentClassName?: string;
};

function BuddyRequestStepLayout({
  header,
  children,
  footer,
  contentClassName,
}: BuddyRequestStepLayoutProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {header ? (
        <div className="shrink-0 bg-background pt-2 pb-4">{header}</div>
      ) : null}
      <div
        className={cn(
          "no-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 pb-4",
          contentClassName,
        )}
      >
        {children}
      </div>
      {footer ? (
        <div className="shrink-0 bg-background/95 pt-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

function SubmitButton({ isPending }: { isPending: boolean }) {
  return (
    <Button
      type="submit"
      disabled={isPending}
      className="h-12 w-full rounded-lg bg-brand text-text-inverse shadow-md transition-all hover:bg-brand-hover hover:shadow-lg"
    >
      {isPending ? "Sending..." : "Send Request"}
    </Button>
  );
}
